/**
 * Hook to sync Navigator library settings with database
 */

import { useCallback, useEffect, useMemo, useRef } from "react"

import debounce from "lodash/debounce"

import { useAIExplorerSettings } from "@/hooks/use-module-settings"
import { useRouteOverrides } from "@/hooks/use-route-overrides"
import { useNavigatorStore } from "@/stores/navigator-store"

/**
 * Sync the navigator library state with the user's saved settings.
 *
 * `pinnedLibraryIds` (optional): when set (array, possibly empty), overrides
 * the user-settings-based initial load with that exact set — used to apply
 * a project's `defaultEnabledIndexIds` pin. User toggles inside the chat
 * still persist back to user settings as normal. Pass `null`/`undefined` for
 * normal behavior.
 */
export function useNavigatorLibrariesSync(sessionType: string, pinnedLibraryIds?: string[] | null) {
  const { settings, updateSettings, isLoading } = useAIExplorerSettings(sessionType)
  const overrides = useRouteOverrides()
  const navigatorStore = useNavigatorStore(sessionType)
  const enabledLibraries = navigatorStore((state) => state.enabledLibraries)
  const setEnabledLibraries = navigatorStore((state) => state.setEnabledLibraries)
  const toggleLibraryChecked = navigatorStore((state) => state.toggleLibraryChecked)
  const toggleAllLibraries = navigatorStore((state) => state.toggleAllLibraries)
  const initLibraries = navigatorStore((state) => state.initLibraries)

  // Stable key for the pin so the load effect re-runs when the pin set
  // actually changes, not on every render (array reference is unstable).
  // JSON-encoded + sorted so it's order-independent and safe for ids that
  // could contain any character (no fragile separator).
  const pinKey = Array.isArray(pinnedLibraryIds)
    ? JSON.stringify([...pinnedLibraryIds].sort())
    : null

  // Keep the live id list available to the load effect via a ref so we don't
  // have to add the unstable array reference to the dep list (pinKey is the
  // stable signal that drives re-runs).
  const pinnedIdsRef = useRef(pinnedLibraryIds)
  pinnedIdsRef.current = pinnedLibraryIds

  // Track the last pinKey we actually applied so within-chat user toggles
  // aren't reverted when the load effect re-runs because
  // `settings.enabled_libraries` changed (the toggle's debounced save updates
  // settings, which is in the dep list). We only re-apply when the pin itself
  // changes, not on every settings refresh.
  const lastAppliedPinKeyRef = useRef<string | null>(null)

  // Create a stable ref for updateSettings to avoid recreating debounced function
  const updateSettingsRef = useRef(updateSettings)
  updateSettingsRef.current = updateSettings

  // Debounced database save (1000ms) to prevent race conditions on rapid toggles
  const debouncedUpdateDB = useMemo(
    () =>
      debounce((libraries: typeof enabledLibraries) => {
        updateSettingsRef.current({ enabled_libraries: libraries })
      }, 2000),
    [] // Empty deps - function is created once and uses ref for latest updateSettings
  )

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateDB.cancel()
    }
  }, [debouncedUpdateDB])

  // Load libraries from database on mount
  useEffect(() => {
    const loadLibraries = async () => {
      // First initialize libraries structure from settings service
      await initLibraries()

      // If a project pin is active, it overrides whatever the user has saved.
      // But: only apply when the pin itself changed — otherwise an in-chat
      // user toggle (which writes settings.enabled_libraries via the debounced
      // sync) would re-fire this effect and the pin would clobber the toggle.
      if (pinKey !== null) {
        if (lastAppliedPinKeyRef.current === pinKey) return
        const pinnedSet = new Set(pinnedIdsRef.current ?? [])
        const structure = navigatorStore.getState().enabledLibraries
        if (Object.keys(structure).length > 0) {
          const pinnedLibraries = Object.fromEntries(
            Object.entries(structure).map(([id, library]) => [
              id,
              { ...library, checked: pinnedSet.has(id) },
            ])
          )
          setEnabledLibraries(pinnedLibraries)
          lastAppliedPinKeyRef.current = pinKey
        }
        return
      }

      // No pin active — clear the "last applied" marker so a future pin
      // (e.g. project gets one set) is treated as a fresh application.
      lastAppliedPinKeyRef.current = null

      const hasSavedSettings =
        settings?.enabled_libraries && Object.keys(settings.enabled_libraries).length > 0

      if (!hasSavedSettings) {
        const initialLibraries = navigatorStore.getState().enabledLibraries
        if (Object.keys(initialLibraries).length > 0) {
          const allEnabledLibraries = Object.fromEntries(
            Object.entries(initialLibraries).map(([id, library]) => [
              id,
              { ...library, checked: true },
            ])
          )
          setEnabledLibraries(allEnabledLibraries)
          // Persist defaults to database so they survive page refresh
          debouncedUpdateDB(allEnabledLibraries)
        }
      }

      // Then override with database settings if they exist
      if (hasSavedSettings) {
        let librariesToSet = settings.enabled_libraries

        // Apply library restriction from route overrides
        if (overrides.restrictToLibraryId) {
          const restrictedLib = overrides.restrictToLibraryId as string
          librariesToSet = Object.fromEntries(
            Object.entries(librariesToSet).filter(([id]) => id === restrictedLib)
          )
        } else if (overrides.restrictToLibraryIds) {
          const allowed = overrides.restrictToLibraryIds as string[]
          librariesToSet = Object.fromEntries(
            Object.entries(librariesToSet).filter(([id]) => allowed.includes(id))
          )
        }

        setEnabledLibraries(librariesToSet)
      }
    }

    void loadLibraries()
  }, [
    settings?.enabled_libraries,
    initLibraries,
    setEnabledLibraries,
    overrides,
    pinKey,
    navigatorStore,
    debouncedUpdateDB,
  ])

  // Sync to database when libraries change in store
  const syncToggleLibrary = useCallback(
    (libraryId: string) => {
      // 1. First update UI optimistically (instant feedback)
      toggleLibraryChecked(libraryId)

      // 2. Then update database with debouncing to prevent race conditions
      // Get the NEW state after toggle
      setTimeout(() => {
        const updatedLibraries = navigatorStore.getState().enabledLibraries
        debouncedUpdateDB(updatedLibraries)
      }, 0)
    },
    [toggleLibraryChecked, debouncedUpdateDB, navigatorStore]
  )

  const syncToggleAllLibraries = useCallback(
    (enable: boolean) => {
      // Calculate new state for database
      const newLibraries = { ...enabledLibraries }
      for (const libraryId in newLibraries) {
        newLibraries[libraryId] = {
          ...newLibraries[libraryId],
          checked: enable,
        }
      }

      // 1. First update UI optimistically (instant feedback)
      toggleAllLibraries(enable)

      // 2. Then update database with debouncing to prevent race conditions
      // Now each variant saves to its own section
      debouncedUpdateDB(newLibraries)
    },
    [enabledLibraries, toggleAllLibraries, debouncedUpdateDB]
  )

  return {
    enabledLibraries,
    toggleLibraryChecked: syncToggleLibrary,
    toggleAllLibraries: syncToggleAllLibraries,
    isLoading,
  }
}
